import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError, setToken, homePathFor } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function CambiarContrasena() {
  const { session, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const forced = !!session?.user?.must_change_password;

  async function onSubmit(e) {
    e.preventDefault();
    setErr(null);
    if (next.length < 10) {
      setErr("La nueva contraseña debe tener al menos 10 caracteres.");
      return;
    }
    if (next !== confirm) {
      setErr("Las contraseñas no coinciden.");
      return;
    }
    if (next === current) {
      setErr("La nueva contraseña debe ser distinta a la actual.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      if (data?.access_token) setToken(data.access_token);
      await refresh();
      navigate(homePathFor(session?.user), { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function onCancel() {
    // En cambio forzado no dejamos entrar al sistema. La única salida es logout.
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6"
         data-testid="change-password-page">
      <div className="w-full max-w-md bg-white rounded-xl border border-border p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <img
            src="/logo-principal.png"
            alt="TallerOS"
            className="h-10 w-10 rounded-md object-contain ring-1 ring-slate-200"
          />
          <div>
            <div className="text-sm font-semibold tracking-tight">TallerOS</div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
              by VRIntegrales
            </div>
          </div>
        </div>

        {forced && (
          <div className="mb-5 rounded-md border border-amber-200 bg-amber-50 p-3 flex gap-2"
               data-testid="change-password-forced-notice">
            <ShieldCheck className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-800 leading-relaxed">
              Por seguridad debes establecer una nueva contraseña antes de continuar.
              Esta contraseña temporal caducará en cuanto la reemplaces.
            </div>
          </div>
        )}

        <h1 className="text-2xl tracking-tight mb-1">Cambiar contraseña</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Ingresa tu contraseña actual y elige una nueva de al menos 10 caracteres.
        </p>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="change-password-form">
          {err && (
            <Alert variant="destructive" data-testid="change-password-error">
              <AlertDescription>{err}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="current">Contraseña actual</Label>
            <Input
              id="current"
              type="password"
              autoComplete="current-password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              data-testid="change-password-current"
              className="h-11"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="next">Nueva contraseña</Label>
            <Input
              id="next"
              type="password"
              autoComplete="new-password"
              required
              minLength={10}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="change-password-new"
              className="h-11"
            />
            <div className="text-[11px] text-muted-foreground">
              Mínimo 10 caracteres. Usa una frase única no reutilizada en otros sitios.
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm">Confirmar nueva contraseña</Label>
            <Input
              id="confirm"
              type="password"
              autoComplete="new-password"
              required
              minLength={10}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="change-password-confirm"
              className="h-11"
            />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-11 btn-tenant"
            data-testid="change-password-submit"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Guardar y continuar
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>

          <button
            type="button"
            onClick={onCancel}
            className="w-full text-xs text-muted-foreground hover:text-slate-900 mt-2"
            data-testid="change-password-cancel"
          >
            Cerrar sesión
          </button>
        </form>
      </div>
    </div>
  );
}
